import React, { useContext, useState, useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import _ from "lodash"
import { useQuery } from "@apollo/client"
import PageHeader from "../../components/PageHeader"
import { SplitButton } from "primereact/splitbutton"
import { LoaderMedium } from "../../components/Loaders"
import { GET_USERS } from "./queries"
import { UserContext } from "../../context/userContext"
import GenericTable from "../../components/DataTable/GenericTable"
import { useFilters } from "../../components/Filters/useFilters"
import FilterHeader from "../../components/DataTable/FilterHeader"
import { Button } from "primereact/button"
import PageSidebarContent from "../../components/sidebars/PageSidebarContent"
import OrganizationMenuSidebar from "../../components/sidebars/OrganizationMenuSidebar"
import StaffGroupSidebar from "../../components/sidebars/StaffGroupSidebar"

const ID_TEMPLATE = "staff"

const ListUserOrg = () => {
  const userCtx = useContext(UserContext)
  const [users, setUsers] = useState([])
  const [roles, setRoles] = useState([])
  const [supervisors, setSupervisors] = useState([])
  const [loading, setLoading] = useState(true)
  const hasClearedStaffOnMountRef = useRef(false)
  const navigate = useNavigate()

  const {
    filters,
    globalFilterValue,
    onGlobalFilterChange,
    clearGlobalFilter,
    onFilterChange,
    clearAllFilters,
  } = useFilters()

  // Clear any stale selected staff once when this list page mounts.
  useEffect(() => {
    if (hasClearedStaffOnMountRef.current) {
      return
    }

    userCtx.clearCurrentStaffMembershipId()
    hasClearedStaffOnMountRef.current = true
  }, [userCtx])

  const { data, loading: queryLoading } = useQuery(GET_USERS, {
    variables: {
      staff_id: userCtx.session.user?.staff_id,
      organization_id: userCtx.session.user?.organization_id,
    },
    skip: !userCtx.session.user.staff_id,
    fetchPolicy: "network-only",
  })

  useEffect(() => {
    if (data) {
      const filterUsersByOrganization = _.filter(data.users, {
        organization_id: userCtx.session.user?.organization_id,
      })

      const rolesOptions = _.map(
        data.users,
        ({ staff_role_name }) => staff_role_name
      )
        .filter((value, index, self) => value && self.indexOf(value) === index)
        .map((role) => ({ label: role, value: role }))

      const supervisorsOptions = _.map(
        data.users,
        ({ staff_primary_supervisor_name }) => staff_primary_supervisor_name
      )
        .filter((value, index, self) => value && self.indexOf(value) === index)
        .map((supervisor) => ({ label: supervisor, value: supervisor }))

      setRoles([{ label: "All", value: "" }, ...rolesOptions])
      setSupervisors([{ label: "All", value: "" }, ...supervisorsOptions])

      setUsers(
        _.map(filterUsersByOrganization, (staff) => {
          const isSelf = staff.staff_id === userCtx.session.user.staff_id // Check if the row corresponds to the logged-in user
          const isSupervisor = userCtx.session.user.staff_role >= 4 // Check if the user's role level is 4 or higher

          const actionsModel =
            isSupervisor && isSelf
              ? [] // No additional actions if both conditions are met
              : [
                  {
                    label: "Edit",
                    command: async () => {
                      await userCtx.changeCurrentStaffMembershipId(
                        staff?.staff_membership_id
                      )
                      navigate(
                        `/organization/form/${ID_TEMPLATE}/1/${staff?.staff_id_stamp}`
                      )
                    },
                  },
                ]

          return {
            ...staff,
            full_name: `${_.unescape(staff.staff_name_first)} ${_.unescape(
              staff.staff_name_last
            )}`,
            actions:
              actionsModel.length > 0 ? (
                <SplitButton
                  label="View"
                  onClick={async () => {
                    await userCtx.changeCurrentStaffMembershipId(
                      staff?.staff_membership_id
                    ) // Set the staff membership ID
                    await userCtx.changeOrganization(staff?.organization_id) // Change the organization
                    navigate("/organization/staff/profile") // Navigate to the profile page
                  }}
                  model={actionsModel}
                />
              ) : (
                <Button
                  label="View"
                  onClick={async () => {
                    await userCtx.changeCurrentStaffMembershipId(
                      staff?.staff_membership_id
                    ) // Set the staff membership ID
                    await userCtx.changeOrganization(staff?.organization_id) // Change the organization
                    navigate("/organization/staff/profile") // Navigate to the profile page
                  }}
                />
              ),
          }
        })
      )

      setLoading(false)
    }
  }, [data, userCtx, userCtx.session.user?.organization_id, navigate])

  const filterConfig = [
    {
      label: "Role",
      value: filters.staff_role_name.value,
      options: roles,
      onChange: (e) => onFilterChange("staff_role_name", e.value),
      onClear: () => onFilterChange("staff_role_name", null),
      enableFilter: true,
    },
    {
      label: "Supervisor",
      value: filters.staff_primary_supervisor_name.value,
      options: supervisors,
      onChange: (e) => onFilterChange("staff_primary_supervisor_name", e.value),
      onClear: () => onFilterChange("staff_primary_supervisor_name", null),
      enableFilter: true,
    },
  ]

  const sidebarContent = (
    <>
      <OrganizationMenuSidebar />
      <br />
      <StaffGroupSidebar />
    </>
  )

  if (loading || queryLoading)
    return (
      <>
        <PageSidebarContent>{sidebarContent}</PageSidebarContent>
        <LoaderMedium />
      </>
    )

  const columns = [
    { field: "full_name", header: "Staff Name", sortable: true },
    { field: "staff_role_name", header: "Role", sortable: true },
    {
      field: "staff_primary_supervisor_name",
      header: "Supervisor",
      sortable: true,
    },
    {
      field: "assigned_participants",
      header: "Assigned Participants",
      sortable: true,
    },
    { field: "actions", header: "Actions" },
  ]

  return (
    <>
      <PageSidebarContent>{sidebarContent}</PageSidebarContent>
      <div className="main-content">
        <PageHeader
          title="Manage Staff Members"
          actions={[
            {
              to: `/organization/form/${ID_TEMPLATE}/1/`,
              label: "Add new member",
            },
          ]}
        />
        <GenericTable
          data={users}
          columns={columns}
          filters={filters}
          globalFilterValue={globalFilterValue}
          onGlobalFilterChange={onGlobalFilterChange}
          header={
            <FilterHeader
              filters={filterConfig}
              globalFilterValue={globalFilterValue}
              onGlobalFilterChange={onGlobalFilterChange}
              clearGlobalFilter={clearGlobalFilter}
              clearAllFilters={clearAllFilters}
            />
          }
          paginator
          rows={50}
          sortField="full_name"
          sortOrder={1}
        />
      </div>
    </>
  )
}

export default ListUserOrg
